import { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { trackSignUp } from '../utils/facebookPixel';
import './SignUpScreen.css';

const SignUpScreen = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Todos os campos são obrigatórios');
      return false;
    }
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor, insira um e-mail válido');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const normalizedEmail = formData.email.toLowerCase().trim();
      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, formData.password);
      const user = userCredential.user;

      await updateProfile(user, { displayName: formData.name });
      await sendEmailVerification(user);

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        name: formData.name,
        email: normalizedEmail,
        emailVerified: false,
        platform: 'Web',
        createdAt: new Date(),
        lastLogin: new Date(),
        plan: 'free'
      });

      setSuccess(true);
      
      // 📊 Rastrear o evento de registro no Facebook Pixel
      trackSignUp();

      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/home');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Erro no cadastro:', err);
      let errorMessage = 'Erro inesperado ao criar conta';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'Este e-mail já está em uso. Tente fazer login.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'A senha é muito fraca. Use pelo menos 6 caracteres.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'E-mail inválido. Verifique o formato.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="signup-container">
        <div className="signup-success-section">
          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h1 className="success-title">Cadastro Concluído!</h1>
            <p className="success-greeting">Bem-vindo(a), <strong>{formData.name}</strong>!</p>
            <p className="success-message">
              Enviamos um e-mail de verificação para <strong>{formData.email}</strong>.
              Por favor, verifique sua caixa de entrada.
            </p>
            <div className="countdown-section">
              <p className="countdown-text">
                Redirecionando para Home em <span className="countdown-number">{countdown}</span> segundos...
              </p>
            </div>
            <button 
              className="success-button"
              onClick={() => navigate('/home')}
            >
              Ir para Home Agora
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <div className="signup-background">
        <div className="signup-card">
          {/* Header com Título */}
          <div className="signup-header">
            <h1 className="signup-title">Lotopim</h1>
            <p className="signup-subtitle">
              Crie sua conta e comece a usar nossas estratégias
            </p>
          </div>

          {/* Formulário */}
          <form className="signup-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="input-group">
              <label htmlFor="name" className="input-label">
                Nome Completo
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Digite seu nome completo"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="email" className="input-label">
                E-mail
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">
                Senha
              </label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Mínimo 6 caracteres"
                value={formData.password}
                onChange={handleChange}
                required
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword" className="input-label">
                Confirmar Senha
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Digite a senha novamente"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="form-input"
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className={`signup-button ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Criando sua conta...
                </>
              ) : (
                'Criar Conta Gratuita'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="signup-footer">
            <p className="footer-text">
              Já tem uma conta?{' '}
              <Link to="/login" className="footer-link">
                Fazer Login
              </Link>
            </p>
            <div className="security-badge">
              <span className="security-icon">🔒</span>
              <span>Seus dados estão seguros conosco</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpScreen;